import { useState, useEffect } from "react";
import api from "../Api";
import { useKvKLookup } from "./useKvKLookup";
import {useAuth} from "../context/authcontext";

export function useProfile(showFlash) {
    const [profile, setProfile] = useState(null);
    const [editableProfile, setEditableProfile] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [vatError, setVatError] = useState("");

    // Image cropper state
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    const baseUrl = (window.env && window.env.REACT_APP_BASE_URL) || process.env.REACT_APP_BASE_URL || "http://localhost:8080";
    const { auth } = useAuth();
    // KvK lookup integration
    const { kvkError, setKvkError, loadingKvk, fetchKvKData } = useKvKLookup(setEditableProfile);

    // Document upload state
    const [file, setFile] = useState(null);
    const [documentCategory, setDocumentCategory] = useState("");
    const [expires, setExpiresAt] = useState("");
    const [documents, setDocuments] = useState([]);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Document expiry editing state
    const [editingDocId, setEditingDocId] = useState(null);
    const [editedExpiry, setEditedExpiry] = useState("");


    useEffect(() => {
        fetchProfile();
        fetchDocuments();
        // eslint-disable-next-line
    }, []);

    // Fetch user profile
    const fetchProfile = async () => {
        try {
            const response = await api.get(`/users/profile`);
            setProfile(response.data);
            setEditableProfile(response.data);
            if (response.data.profile_picture) {
                setPreviewImage(`${response.data.profile_picture}`);
            }
        } catch (err) {
            setError("Kan profiel niet ophalen: " + (err.response?.data?.error || err.message));
        }
    };

    // Fetch user documents
    const fetchDocuments = async () => {
        try {
            const res = await api.get(`/users/${auth.userId}/documents`);
            setDocuments(res.data || []);
        } catch (err) {
            setDocuments([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditableProfile((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditClick = () => setIsEditing(true);
    const handleCancelClick = () => {
        setEditableProfile(profile);
        setIsEditing(false);
    };

    const handleSaveClick = () => {
        console.log('handleSaveClick called');
        console.log('selectedFile:', selectedFile);
        setShowConfirmModal(true);
    };

    const validateDutchVat = (vat) => /^NL\d{9}B\d{2}$/.test(vat);

    const handleVatChange = (e) => {
        handleInputChange(e);
        const value = e.target.value;
        if (value && !validateDutchVat(value)) {
            setVatError("Ongeldig Nederlands BTW-nummer (bijv. NL123456789B01)");
        } else {
            setVatError("");
        }
    };

    const confirmSave = async () => {
        console.log('🔵 confirmSave called');
        console.log('📁 selectedFile:', selectedFile);

        if (kvkError || vatError) {
            showFlash && showFlash("Corrigeer eerst de KvK- of BTW-nummer fouten.", "error");
            return;
        }
        try {
            console.log('💾 Saving profile data...');

            // Create a copy of editableProfile without profile_picture
            // Profile picture is only updated via separate upload endpoint
            const { profile_picture, ...profileDataToUpdate } = editableProfile;

            // Save profile data (excluding profile_picture)
            await api.put(`/users/${profile.id}/profile`, profileDataToUpdate);
            console.log('✅ Profile data saved');

            // Upload profile picture if selected
            if (selectedFile) {
                console.log('📸 Profile picture selected, starting upload...');
                console.log('📏 File size:', (selectedFile.size / (1024 * 1024)).toFixed(2), 'MB');

                // Double-check file size before upload
                const maxSize = 5 * 1024 * 1024; // 5MB
                if (selectedFile.size > maxSize) {
                    const sizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
                    console.log('❌ File too large:', sizeMB, 'MB');
                    showFlash && showFlash(
                        `Profielfoto is te groot (${sizeMB}MB). Maximum is 5MB.`,
                        "error"
                    );
                    setShowConfirmModal(false);
                    return;
                }

                console.log('📦 Creating FormData...');
                const formData = new FormData();
                formData.append("profile_picture", selectedFile);
                console.log('📤 Uploading to /users/profile-picture...');

                try {
                    const uploadResponse = await api.post("/users/profile-picture", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                        maxContentLength: 10 * 1024 * 1024, // 10MB max for request
                        maxBodyLength: 10 * 1024 * 1024
                    });
                    console.log('✅ Upload successful!', uploadResponse.data);
                } catch (uploadErr) {
                    console.error("❌ Profile picture upload error:", uploadErr);

                    // Handle specific errors
                    if (uploadErr.response) {
                        const status = uploadErr.response.status;
                        const errorMessage = uploadErr.response.data?.error || uploadErr.response.data;

                        if (status === 400) {
                            if (typeof errorMessage === 'string' && errorMessage.includes('too large')) {
                                showFlash && showFlash(
                                    "Bestand is te groot. Maximum is 5MB. Comprimeer de foto.",
                                    "error"
                                );
                            } else if (typeof errorMessage === 'string' && errorMessage.includes('Invalid file')) {
                                showFlash && showFlash(
                                    "Ongeldig bestand. Alleen afbeeldingen zijn toegestaan.",
                                    "error"
                                );
                            } else {
                                showFlash && showFlash(
                                    `Upload mislukt: ${errorMessage}`,
                                    "error"
                                );
                            }
                        } else if (status === 413) {
                            showFlash && showFlash(
                                "Bestand is te groot voor de server. Comprimeer de foto tot onder 5MB.",
                                "error"
                            );
                        } else if (status === 500) {
                            showFlash && showFlash(
                                "Server fout bij uploaden. Probeer later opnieuw.",
                                "error"
                            );
                        } else {
                            showFlash && showFlash(
                                `Upload mislukt (${status}). Probeer opnieuw.`,
                                "error"
                            );
                        }
                    } else if (uploadErr.code === 'ERR_NETWORK' || uploadErr.code === 'ERR_CONNECTION_CLOSED') {
                        showFlash && showFlash(
                            "Netwerkfout. Controleer je internetverbinding en probeer opnieuw.",
                            "error"
                        );
                    } else {
                        showFlash && showFlash(
                            "Foto uploaden mislukt. Probeer opnieuw.",
                            "error"
                        );
                    }

                    setShowConfirmModal(false);
                    return;
                }
            }

            // Refresh profile to get latest data
            await fetchProfile();

            setProfile(editableProfile);
            setIsEditing(false);
            setShowConfirmModal(false);
            setSelectedFile(null); // Clear selected file after upload

            showFlash && showFlash(
                selectedFile
                    ? "Profiel en foto succesvol bijgewerkt!"
                    : "Profiel succesvol bijgewerkt.",
                "success"
            );
        } catch (err) {
            console.error("Profile update error:", err);
            const errorMsg = err.response?.data?.error || err.message || "Onbekende fout";
            showFlash && showFlash(
                `Bijwerken mislukt: ${errorMsg}. Probeer opnieuw.`,
                "error"
            );
            setShowConfirmModal(false);
        }
    };

    const handleFileChange = (e) => {
        console.log('handleFileChange called, files:', e.target.files);
        const file = e.target.files[0];
        console.log('Selected file:', file);
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                console.log('Invalid file type:', file.type);
                showFlash && showFlash(
                    "Ongeldig bestandstype. Alleen JPG, PNG, GIF en WebP zijn toegestaan.",
                    "error"
                );
                e.target.value = ''; // Clear input
                return;
            }

            // Validate file size (max 10MB for initial selection, will be compressed)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
                console.log('File too large:', sizeMB, 'MB');
                showFlash && showFlash(
                    `Bestand is te groot (${sizeMB}MB). Maximum is 10MB.`,
                    "error"
                );
                e.target.value = ''; // Clear input
                return;
            }

            console.log('File valid, opening cropper...');
            // Read file and open cropper
            const reader = new FileReader();
            reader.onload = () => {
                console.log('FileReader onload, setting imageToCrop');
                setImageToCrop(reader.result);
                setShowImageCropper(true);
                console.log('showImageCropper set to true');
            };
            reader.readAsDataURL(file);

            // Clear file input
            e.target.value = '';
        } else {
            console.log('No file selected');
        }
    };

    const handleCropComplete = async (croppedAreaPixels, rotation) => {
        console.log('🎨 handleCropComplete called', { croppedAreaPixels, rotation });
        try {
            console.log('📦 Importing cropImage utils...');
            const { createCroppedImage, blobToFile } = await import('../utils/cropImage');

            console.log('✂️ Creating cropped image...');
            const croppedBlob = await createCroppedImage(
                imageToCrop,
                croppedAreaPixels,
                rotation
            );

            console.log('🔄 Converting blob to file...');
            // Convert blob to file
            const croppedFile = blobToFile(croppedBlob, 'profile-photo.jpg');

            // Check final size
            const finalSizeMB = (croppedFile.size / (1024 * 1024)).toFixed(2);
            console.log(`📏 Cropped image size: ${finalSizeMB}MB`, croppedFile);

            // Set the cropped file for upload
            console.log('💾 Setting selectedFile to:', croppedFile);
            setSelectedFile(croppedFile);
            console.log('🖼️ Setting preview image...');
            setPreviewImage(URL.createObjectURL(croppedFile));
            setShowImageCropper(false);
            setImageToCrop(null);
            console.log('✅ Crop complete! selectedFile is now set');

            showFlash && showFlash(
                `Foto geselecteerd (${finalSizeMB}MB). Klik op "Opslaan" om te uploaden.`,
                "success"
            );
        } catch (error) {
            console.error('❌ Crop error:', error);
            showFlash && showFlash(
                "Fout bij bijsnijden van foto. Probeer opnieuw.",
                "error"
            );
            setShowImageCropper(false);
            setImageToCrop(null);
        }
    };

    const handleCropCancel = () => {
        setShowImageCropper(false);
        setImageToCrop(null);
    };

    // Document expiry editing functions
    const handleEditExpiry = (doc) => {
        setEditingDocId(doc.id);
        setEditedExpiry(doc.expires_at ? new Date(doc.expires_at).toISOString().split('T')[0] : "");
    };

    const handleSaveExpiry = async (docId) => {
        try {
            const response = await api.put(`/users/${profile.id}/documents/${docId}/expiry`, {
                expires_at: editedExpiry ? new Date(editedExpiry).toISOString() : null
            });

            if (response.status === 200) {
                showFlash && showFlash("Vervaldatum succesvol bijgewerkt!", "success");
                setEditingDocId(null);
                setEditedExpiry("");
                // Refresh documents
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error updating expiry:', error);
            const errorMessage = error.response?.data?.error || error.message || "Onbekende fout";
            showFlash && showFlash(`Fout bij opslaan vervaldatum: ${errorMessage}`, "error");
        }
    };

    const handleCancelEditExpiry = () => {
        setEditingDocId(null);
        setEditedExpiry("");
    };

    const handleUploadProfilePicture = async (selectedFile) => {
        if (!selectedFile) {
            showFlash?.("Geen bestand geselecteerd.", "error");
            return;
        }

        // Validate file size
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (selectedFile.size > maxSize) {
            const sizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
            showFlash?.(`Bestand is te groot (${sizeMB}MB). Maximum is 5MB.`, "error");
            return;
        }

        const formData = new FormData();
        formData.append("profile_picture", selectedFile);

        try {
            await api.post("/users/profile-picture", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                maxContentLength: 10 * 1024 * 1024,
                maxBodyLength: 10 * 1024 * 1024
            });
            showFlash?.("Profielfoto succesvol geüpload!", "success");
            fetchProfile();
        } catch (err) {
            console.error("Profile picture upload error:", err);

            if (err.response) {
                const status = err.response.status;
                const errorMessage = err.response.data?.error || err.response.data;

                if (status === 400 && typeof errorMessage === 'string' && errorMessage.includes('too large')) {
                    showFlash?.("Bestand te groot. Maximum is 5MB.", "error");
                } else if (status === 413) {
                    showFlash?.("Bestand te groot voor server. Gebruik een kleinere foto.", "error");
                } else {
                    showFlash?.(`Upload mislukt: ${errorMessage}`, "error");
                }
            } else {
                showFlash?.("Fout bij uploaden van profielfoto.", "error");
            }
        }
    };

    // Wrapper function for the upload button
    const handleUpload = () => {
        handleUploadProfilePicture(selectedFile);
    };

    // Document upload handler
    const onDocumentUpload = async (file, documentCategory, expires) => {
        if (!file || !documentCategory) {
            showFlash && showFlash("Selecteer een bestand en type.", "warning");
            return;
        }
        const formData = new FormData();
        formData.append("document", file);
        formData.append("document_category", documentCategory);
        if (expires) formData.append("expires_at", new Date(expires).toISOString());
        try {
            await api.post(`/users/${profile.id}/documents/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            showFlash && showFlash("Document succesvol geüpload!", "success");
            setFile(null);
            setDocumentCategory("");
            setExpiresAt("");
            setShowUploadForm(false);
            fetchDocuments();
        } catch (err) {
            showFlash && showFlash("Uploaden van document is mislukt.", "error");
        }
    };

    // Document delete handler
    const onDeleteUserDocument = async (docId) => {
        try {
            await api.delete(`/users/${profile.id}/documents/${docId}`);
            showFlash && showFlash("Document verwijderd.", "success");
            fetchDocuments();
        } catch (err) {
            showFlash && showFlash("Verwijderen mislukt.", "error");
        }
    };

    return {
        profile,
        editableProfile,
        setEditableProfile,
        selectedFile,
        setSelectedFile,
        previewImage,
        setPreviewImage,
        isEditing,
        setIsEditing,
        error,
        setError,
        showConfirmModal,
        setShowConfirmModal,
        handleInputChange,
        handleEditClick,
        handleCancelClick,
        handleSaveClick,
        confirmSave,
        handleFileChange,
        handleUploadProfilePicture,
        handleUpload,
        fetchKvKData,
        kvkError,
        setKvkError,
        loadingKvk,
        handleVatChange,
        vatError,
        setVatError,
        file,
        setFile,
        documentCategory,
        setDocumentCategory,
        expires,
        setExpiresAt,
        documents,
        setDocuments,
        showUploadForm,
        setShowUploadForm,
        selectedDocId,
        setSelectedDocId,
        showDeleteModal,
        setShowDeleteModal,
        onDocumentUpload,
        onDeleteUserDocument,
        baseUrl,
        fetchDocuments, // Export this so components can refresh documents
        // Image cropper
        showImageCropper,
        imageToCrop,
        handleCropComplete,
        handleCropCancel,
        // Document expiry editing
        editingDocId,
        editedExpiry,
        setEditedExpiry,
        handleEditExpiry,
        handleSaveExpiry,
        handleCancelEditExpiry,
    };
}
